<script lang="ts">
  // Root router component.
  //
  // Takes a route tree, watches `history.pathname`, finds the matching
  // chain via `matchRoutes`, then renders the chain — each level's
  // component receives the next level as its `children` snippet, so
  // layout components compose naturally.
  //
  // Lazy components are loaded on first match and cached in memory.
  // While loading, the slot renders the `loading` snippet (or a
  // tiny default placeholder). Subsequent navigations to the same
  // component are synchronous from the cache.

  import type { Component, Snippet } from "svelte";
  import { SvelteMap } from "svelte/reactivity";
  import { history } from "./history.svelte";
  import { matchRoutes } from "./match";
  import { isLazy, type LazyComponent } from "./lazy";
  import type { Route } from "./types";
  import { _setParams } from "./use-route.svelte";

  // Components declare params optionally. The Router always passes
  // params + (when nested) children; components that don't declare
  // them just ignore. `any` here is the practical choice — making
  // every leaf view declare a never-used `params` prop is friction.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type RouteComponent = Component<any>;

  interface Props {
    routes: Route[];
    /** Rendered when no route in the table matches the current path. */
    notFound?: Snippet;
    /** Rendered while a lazy component is fetching. */
    loading?: Snippet;
  }

  let { routes, notFound, loading }: Props = $props();

  const matches = $derived(matchRoutes(routes, history.pathname));

  // Cache resolved lazy components by their `LazyComponent` identity.
  // `SvelteMap` is the reactive variant — a plain `$state(new Map())`
  // does NOT track `.set()` / `.get()` calls in Svelte 5, so the UI
  // would never re-render once a chunk resolved.
  const loaded = new SvelteMap<LazyComponent, RouteComponent>();

  // Pre-load any unresolved lazy components in the matched chain.
  // Runs whenever `matches` changes.
  $effect(() => {
    if (!matches) return;
    for (const m of matches) {
      const c = m.route.component;
      if (isLazy(c) && !loaded.has(c)) {
        c.load().then((resolved) => {
          loaded.set(c, resolved as RouteComponent);
        });
      }
    }
  });

  // Publish the leaf params to the module-scoped `route` store so
  // any component deep in the tree can read them.
  $effect(() => {
    if (matches && matches.length > 0) {
      _setParams(matches[matches.length - 1].params);
    } else {
      _setParams({});
    }
  });

  function resolve(r: Route): RouteComponent | null {
    if (isLazy(r.component)) return loaded.get(r.component) ?? null;
    return r.component as RouteComponent;
  }
</script>

{#if matches}
  {@render chain(0)}
{:else if notFound}
  {@render notFound()}
{:else}
  <div class="sb-router-no-match">No route matched: <code>{history.pathname}</code></div>
{/if}

{#snippet chain(idx: number)}
  {#if matches && idx < matches.length}
    {@const m = matches[idx]}
    {@const Comp = resolve(m.route)}
    {#if Comp}
      <Comp params={m.params}>
        {#if idx + 1 < matches.length}
          {@render chain(idx + 1)}
        {/if}
      </Comp>
    {:else if loading}
      {@render loading()}
    {:else}
      <div class="sb-router-loading" aria-busy="true">Loading…</div>
    {/if}
  {/if}
{/snippet}

<style>
  .sb-router-no-match {
    padding: var(--sb-space-6);
    color: var(--sb-fg-2);
    font-size: var(--sb-text-sm);
  }
  .sb-router-no-match code {
    background-color: var(--sb-surface-3);
    padding: 1px 5px;
    border-radius: var(--sb-radius-sm);
    font-family: var(--sb-font-mono);
    color: var(--sb-fg-1);
  }
  .sb-router-loading {
    padding: var(--sb-space-6);
    color: var(--sb-fg-3);
    font-size: var(--sb-text-sm);
  }
</style>
